/**
 * Clinical instruments (ZOY-599) — UI chrome only. Every clinical wording
 * (instrument name, items, option labels, scale names) comes from the
 * bundle the administration is pinned to and is never translated here.
 */
export const instrumentsEn: Record<string, string> = {
  /* Record section */
  'inst.sectionTitle': 'Clinical instruments',
  'inst.sectionSub':
    'Standardized questionnaires administered to this patient and scored by the system. Independent of assessments and sessions.',
  'inst.start': 'Administer an instrument',
  'inst.series': 'Results over time',
  'inst.emptyTitle': 'No instrument administered yet',
  'inst.emptyBody':
    'A standardized instrument is answered by a caregiver or the patient, recorded by staff, and scored against its normative tables when finalized.',
  'inst.emptyGate': 'Administering an instrument needs a registered patient record.',
  'inst.drafts': '{n, plural, one {# draft in progress} other {# drafts in progress}}',
  'inst.continue': 'Continue',
  'inst.view': 'View',
  'inst.status.draft': 'Draft',
  'inst.status.finalized': 'Finalized',
  'inst.status.superseded': 'Superseded',
  'inst.respondent.caregiver': 'Caregiver',
  'inst.respondent.patient': 'Patient (self)',
  'inst.respondent.other': 'Other',
  'inst.age':
    '{y, plural, one {# year} other {# years}}{m, plural, =0 {} one { # month} other { # months}}',
  'inst.ageUnknown': 'age unknown',
  'inst.tally.scored': '{n, number} scored',
  'inst.tally.rawOnly': '{n, number} raw only',
  'inst.tally.notScorable': '{n, number} not scorable',
  'inst.startedLine': 'Started {date} by {who}',
  'inst.finalizedLine': 'Finalized {date} by {who}',
  'inst.supersededLine': 'Replaced by a corrected administration',
  'inst.correctsLine': 'Corrects an earlier administration',

  /* Start page */
  'inst.newTitle': 'Administer an instrument',
  'inst.newSub':
    'Choose the instrument, say who answers, and set the date. The staff member recording is you — noted automatically.',
  'inst.pickInstrument': 'Instrument',
  'inst.pickInstrumentHelp':
    'Pick one to continue. Each card shows who reports, the ages it covers, and whether it has a normative table.',
  'inst.pickFirst': 'Choose an instrument above to continue.',
  'inst.noInstruments': 'No instrument is available to this clinic yet.',
  'inst.catalogueFailed': 'The instrument list could not be loaded.',
  'inst.selected': 'Selected',
  'inst.forAges': 'For ages {from} to {to}',
  'inst.forAgesFrom': 'From age {from}',
  'inst.forAgesTo': 'Up to age {to}',
  'inst.designedFor.caregiver': 'Caregiver report',
  'inst.designedFor.self': 'Self report',
  'inst.designedFor.either': 'Caregiver or self report',
  'inst.norms': 'Norms: {list}',
  'inst.noNorms': 'No normative table — raw scores only',
  'inst.detailsTitle': 'Respondent and date',
  'inst.detailsSub':
    'Who answered the questions, and when. The recorder is you and is not asked here.',
  'inst.respondentType': 'Who answers',
  'inst.respondentHelp': 'The person whose answers are recorded — not the staff member typing.',
  'inst.relationship': 'Relationship to the patient',
  'inst.relationshipHelp': 'For example mother, father, teacher.',
  'inst.administeredOn': 'Administration date',
  'inst.administeredOnHelp': 'Age on this date selects the normative table.',
  'inst.locale': 'Wording language',
  'inst.localeHelp': 'The wording the instrument is presented in. Pinned for this administration.',
  'inst.localeName.en': 'English',
  'inst.localeName.fa': 'Persian',
  'inst.ageAtDate': 'Age on that date: {age}',
  'inst.dobEstimated': 'The date of birth is recorded as approximate.',
  'inst.warnAgeUnknownTitle': 'No date of birth on record',
  'inst.warnAgeUnknownBody':
    'Age at administration cannot be calculated, so a normative classification will not be available. Raw scores are still recorded. You can continue.',
  'inst.warnAgeRangeTitle': 'Outside the instrument’s stated age range',
  'inst.warnAgeRangeBody':
    'This instrument is designed for {range}; the patient will be {age} on the administration date. A normative classification may not apply. You can continue.',
  'inst.startCta': 'Start the administration',
  'inst.startNote': 'Answers save as you go. Nothing is scored until you finalize.',
  'inst.err.notAvailable':
    'That instrument is no longer available to start in the chosen wording. The list has been refreshed.',
  'inst.err.date': 'That date could not be used.',

  /* Administration page */
  'inst.versionChip': 'Version {n}',
  'inst.wordingChip': '{lang} wording',
  'inst.viewOnly': 'View only',
  'inst.recorder': 'Recorded by',
  'inst.factRespondent': 'Respondent',
  'inst.factDate': 'Administered',
  'inst.factAge': 'Age at administration',
  'inst.saveDetails': 'Save details',
  'inst.detailsSaved': 'Details saved.',
  'inst.unsupportedTitle': 'This instrument can’t be displayed here',
  'inst.unsupportedBody':
    'It uses a newer definition format than this version of the app understands. Nothing about the answers is lost or changed — displaying it needs an updated app.',
  'inst.answeredOf': '{answered} of {total} answered',
  'inst.openCount': '{n, plural, one {# unanswered} other {# unanswered}}',
  'inst.allAnswered': 'All items answered',
  'inst.na': 'Not applicable',
  'inst.naHint': 'An item marked not applicable leaves every scale it belongs to without a score.',
  'inst.clear': 'Clear the answer',
  'inst.note': 'Note',
  'inst.addNote': 'Add a note',
  'inst.noAnswer': 'Not answered',
  'inst.sectionComment': 'Section comment',
  'inst.overallComment': 'Overall comment',
  'inst.commentsTitle': 'Comments',
  'inst.commentsSub': 'Qualitative remarks are part of the instrument and save with it.',
  'inst.saving': 'Saving…',
  'inst.saved': 'Saved',
  'inst.saveFailed': 'Not saved — your entries are kept here.',
  'inst.retry': 'Try again',
  'inst.saveStale':
    'This draft is no longer live — it was finalized or superseded elsewhere. Reopen it to see the current state.',
  'inst.saveRejected':
    'One answer was not accepted, so that change was not saved. Everything on screen is kept — adjust it and it will save again.',
  'inst.saveForbidden':
    'Your permission to record this administration has ended. Nothing was saved.',
  'inst.finalize': 'Finalize',
  'inst.finalizeTitle': 'Finalize this administration?',
  'inst.finalizeBody':
    'Scores are computed against the pinned normative table and the record becomes read-only.',
  'inst.finalizeUnanswered':
    '{n, plural, one {# item is} other {# items are}} unanswered. Finalizing is still valid: the scales those items belong to are reported as not scorable — never as zero.',
  'inst.finalizeEffectTitle': 'What this does',
  'inst.finalizeEffect':
    'Answers and comments freeze. A later correction is made by superseding — this record stays visible as finalized.',
  'inst.finalizeCta': 'Finalize',
  'inst.finalizeFlushFailed':
    'Finalizing stopped: the latest answers could not be saved. Nothing you entered was lost — retry saving, then finalize again.',
  'inst.finalizedSub': 'Shown exactly as finalized — answers, wording and results are frozen.',
  'inst.supersededTitle': 'This administration was superseded',
  'inst.supersededBody':
    'A corrected administration replaced it on {date}. This record stays readable as it was finalized.',
  'inst.openSuperseding': 'Open the corrected administration',
  'inst.correctsTitle': 'This administration corrects an earlier one',
  'inst.correctsBody': 'The original remains on the record, marked as superseded.',
  'inst.openOriginal': 'Open the original',
  'inst.correct': 'Correct by superseding',
  'inst.correctTitle': 'Correct this administration?',
  'inst.correctBody':
    'A new draft is created from this one — same answers, respondent and date — for you to amend and finalize again.',
  'inst.correctEffect':
    'This finalized administration is not changed or deleted: it stays on the record, marked as superseded, with its results.',
  'inst.correctReason': 'Reason for the correction',
  'inst.correctReasonHelp': 'Required. Kept with the record.',
  'inst.correctCta': 'Create the correcting draft',
  'inst.err.notDraft':
    'This administration is no longer a draft — it was finalized or superseded elsewhere. The page has been refreshed.',
  'inst.err.notFinalized': 'Only a finalized administration can be corrected.',
  'inst.err.alreadySuperseded':
    'This administration was already corrected. Open the corrected one from the record.',
  'inst.err.notFound': 'This administration could not be found.',
  'inst.err.forbidden': 'Your permission for this action has ended.',
  'inst.err.commentNotAllowed': 'This instrument version does not take that kind of comment.',

  /* Results */
  'inst.resultsTitle': 'Results',
  'inst.resultsSub':
    'Each scale reports exactly one outcome. A missing score is shown as missing — never as zero or as a typical band.',
  'inst.outcome.scored': 'Scored',
  'inst.outcome.raw_only': 'Raw score only',
  'inst.outcome.not_scorable': 'Not scorable',
  'inst.rawOf': 'of {max}',
  'inst.noClassification': 'No normative classification',
  'inst.noScore': 'No score',
  'inst.reason.missing_items': 'Unanswered items',
  'inst.reason.not_applicable_items': 'Items marked not applicable',
  'inst.reason.no_norm_set': 'No normative table applies to this respondent and age',
  'inst.reason.age_unknown': 'Age at administration is unknown, so no age-banded norms apply',
  'inst.reason.classification_unavailable': 'The normative table has no band for this score',
  'inst.reason.sex_unknown': "The patient's gender is not recorded, so no sex-normed table applies",
  'inst.standardScore.T': 'T-score {n}',
  'inst.standardScore.other': '{type} {n}',
  'inst.percentile': 'Percentile rank {n}',
  'inst.offendingItems': 'Items',
  'inst.displayOnly': 'Grouping only — produces no score',
  'inst.scaleType.section': 'Sections',
  'inst.scaleType.factor': 'Factors',
  'inst.scaleType.quadrant': 'Quadrants',
  'inst.scaleType.total': 'Total',
  'inst.scaleType.sensory_section': 'Sensory sections',
  'inst.scaleType.behavioral_section': 'Behavioural sections',
  'inst.scaleType.sensory_category': 'Sensory categories',
  'inst.direction.typical': 'Typical',
  'inst.direction.more_than_others': 'More than others',
  'inst.direction.less_than_others': 'Less than others',
  'inst.basisTitle': 'Normative basis',
  'inst.basisSub':
    'Classifications are relative to this population only. The wording language never implies a norm population.',
  'inst.basisNone': 'No normative basis was available — raw scores only.',
  'inst.basisPopulation': 'Population',
  'inst.basisAges': 'Age band',
  'inst.basisRespondent': 'Respondent',
  'inst.basisModel': 'Classification model',
  'inst.basisSample': 'Sample size',
  'inst.basisSource': 'Source',
  'inst.basisCompleteness': 'Completeness',
  'inst.normRespondent.caregiver': 'Caregiver',
  'inst.normRespondent.self': 'Self',
  'inst.normRespondent.any': 'Any',
  'inst.basisSex': 'Sex',
  'inst.normSex.male': 'Boys',
  'inst.normSex.female': 'Girls',
  'inst.normSex.any': 'Any',
  'inst.answersTitle': 'Questionnaire',
  'inst.qSub':
    'Work through one section at a time. Every item saves as you go; the rail shows what is left in each section.',
  'inst.sectionsNav': 'Questionnaire sections',
  'inst.sectionOf': 'Section {n} of {total}',
  'inst.sectionDone': 'Complete',
  'inst.sectionLeft': '{n, plural, one {# left} other {# left}}',
  'inst.sectionEmpty': 'Not started',
  'inst.commentsStep': 'Comments',
  'inst.prevSection': 'Previous section',
  'inst.nextSection': 'Next section',
  'inst.toComments': 'Go to comments',
  'inst.nextUnanswered': 'Next unanswered',
  'inst.progressLine': '{answered} of {total} answered · {pct}%',
  'inst.readyToFinalize': 'All items answered — ready to finalize.',
  'inst.stillAllowed':
    'Finalizing is still allowed; unanswered items make their scales not scorable.',
  'inst.backToRecord': 'Back to the patient record',
  'inst.patientFile': 'File {n}',
  'inst.patientLoading': 'Loading the patient…',

  /* Series */
  'inst.seriesTitle': 'Instrument results over time',
  'inst.seriesSub':
    'Finalized administrations, grouped by what may be compared: one instrument, one version group, one normative table.',
  'inst.seriesEmpty':
    'No finalized administration yet — the series starts with the first one finalized.',
  'inst.seriesFailed': 'The series could not be loaded.',
  'inst.incomparableTitle': 'These groups are not comparable with each other',
  'inst.incomparableBody':
    'Different instruments, versions or normative tables are different measurements — some even score frequent behaviour in opposite directions. Read each group on its own.',
  'inst.groupNorm': 'Norms: {population}',
  'inst.groupNoNorm': 'No normative table — raw scores only',
  'inst.groupVersion': 'Comparability group {g}',
  'inst.groupCount': '{n, plural, one {# administration} other {# administrations}}',
  'inst.colScale': 'Scale',
  'inst.seriesRawOnly': 'raw only',
  'inst.seriesGap': 'not scorable',

  /* Age-form families (ASQ) */
  'inst.scaleType.domain': 'Domains',
  'inst.direction.monitor': 'Monitor and re-screen',
  'inst.direction.refer': 'Refer for further assessment',
  'inst.familyForms': '{n} age forms, {from} to {to}',
  'inst.formLabel': 'Age form',
  'inst.formHelp': 'Chosen from the child’s age; pick another form if you need to.',
  'inst.formPick': 'Choose a form',
  'inst.formWindow': '{from} to {to}',
  'inst.pickFormFirst': 'Choose the age form to continue.',
  'inst.ageMd': '{m} months {d} days',
  'inst.ageForForm': 'Age on this date: {age}',
  'inst.ageAdjusted': 'Age adjusted for prematurity: {age}.',
  'inst.formSuggested': 'The form for this age: {form}.',
  'inst.formNoSuggestion':
    'This age is outside every form’s window; choose a form yourself if you need to.',
  'inst.formOverrideTitle': 'The chosen form does not match the child’s age',
  'inst.formOverrideBody':
    'You chose {form}, while the form for this age is {suggested}. Each form’s cut-offs belong to its own age window.',
  'inst.gestationalAge': 'Gestational age at birth (weeks)',
  'inst.gestationalAgeHelp':
    'Age is adjusted only when the child was born before 37 weeks and is under 2 years old.',
  'inst.gestationalInvalid': 'Enter a number between 20 and 44, or leave it empty.',
  'inst.factGestational': 'Gestation',
  'inst.factAdjustedAge': 'Adjusted age',
  'inst.weeks': '{n} weeks',
  'inst.instructionsTitle': 'How to complete',
  'inst.illustration': 'Picture for this item',
  'inst.concern': 'This is a concern',
  'inst.impliedHint':
    'If item {n} is answered {options}, this item is scored as “{answer}” automatically.',
  'inst.orJoin': ' or ',
  'inst.supplementaryStep': 'Overall',
  'inst.supplementarySub':
    'These questions are not scored; answers that deserve attention are marked for the clinician.',
  'inst.suppYes': 'Yes',
  'inst.suppNo': 'No',
  'inst.suppExplain': 'Explanation',
  'inst.suppFlag': 'Needs attention',
  'inst.adjustedScore': 'Adjusted score: {n} ({k} unanswered replaced by the mean answer)',
  'inst.flagsTitle': 'Points to note',
  'inst.flagsSub':
    'Beyond the scores: answers that can justify follow-up or referral even when the scores are typical.',
  'inst.flagsSupplementary': 'Notable answers in the Overall section',
  'inst.flagsConcern': 'Items the respondent marked as a concern',
};
